import { FC } from 'react';
import { usePostHog } from '@posthog/react';
import { useTranslation } from 'react-i18next';
import { Button, Alert } from 'react-daisyui';
import Modal, { ModalProps } from '@/components/modals/Modal/Modal.tsx';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton.tsx';
import useDeleteUserAccount from '@/api/users/useDeleteUserAccount.ts';
import { useAuthContext } from '@/context/AuthContext.tsx';

const DeleteAccountModal: FC<ModalProps> = ({ onClose, ...modalProps }) => {
  const { t } = useTranslation();
  const posthog = usePostHog();
  const { logout } = useAuthContext();
  const { mutateAsync: deleteUserAccount, isPending: isLoading, error } = useDeleteUserAccount();

  const handleConfirm = async () => {
    try {
      await deleteUserAccount();
      posthog?.capture('account_deleted');
      logout();
      onClose?.();
    } catch (error) {
      console.error('Error deleting account:', error);
    }
  };

  return (
    <Modal.Wrapper wrapperClassName="w-[400px]" {...modalProps} onClose={onClose} showCloseButton={false} closeable>
      <Modal.Title>
        <div className="flex flex-col gap-1 text-center">
          <h2 className="font-feature font-bold italic text-2xl">{t('deleteAccountModal.title')}</h2>
          <p className="text-sm text-text-secondary">{t('deleteAccountModal.subtitle')}</p>
        </div>
      </Modal.Title>
      <div className="flex flex-col gap-3">
        {error ? <Alert status="error">{t('deleteAccountModal.error')}</Alert> : null}
      </div>
      <Modal.Nav>
        {({ handleClose }) => (
          <div className="flex flex-col gap-4 w-full">
            <LoadingButton loading={isLoading} type="button" color="error" className="w-full" onClick={handleConfirm}>
              {t('button.delete_account')}
            </LoadingButton>
            <Button type="button" color="ghost" className="text-primary" onClick={handleClose}>
              {t('button.cancel')}
            </Button>
          </div>
        )}
      </Modal.Nav>
    </Modal.Wrapper>
  );
};

export default DeleteAccountModal;

